import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { adminApi } from '../services/api'
import {
  ArrowLeft,
  Building,
  Users,
  FileText,
  Calendar,
  Mail,
  Phone,
  Globe,
  MapPin,
  Loader2,
  CheckCircle,
  XCircle,
  Pause,
  Clock,
  PlayCircle,
  AlertTriangle,
  TrendingUp,
  Euro,
  UserCheck,
  UserX,
  Building2,
  ArrowRightLeft,
  RefreshCw,
  Eye
} from 'lucide-react'

// Status Badge
const StatusBadge = ({ status, size = 'md' }) => {
  const config = {
    active: { 
      style: 'bg-green-100 text-green-700', 
      icon: CheckCircle,
      label: 'Attiva'
    },
    trial: { 
      style: 'bg-blue-100 text-blue-700', 
      icon: Clock,
      label: 'Trial'
    },
    suspended: { 
      style: 'bg-yellow-100 text-yellow-700', 
      icon: Pause,
      label: 'Sospesa'
    },
    pending_deletion: { 
      style: 'bg-red-100 text-red-700', 
      icon: AlertTriangle,
      label: 'In Cancellazione'
    },
    cancelled: { 
      style: 'bg-gray-100 text-gray-700', 
      icon: XCircle,
      label: 'Cancellata'
    },
    deleted: { 
      style: 'bg-gray-100 text-gray-700', 
      icon: XCircle,
      label: 'Eliminata'
    }
  }

  const { style, icon: Icon, label } = config[status] || config.active
  const sizeClass = size === 'lg' ? 'px-3 py-1 text-sm' : 'px-2.5 py-0.5 text-xs'

  return (
    <span className={`inline-flex items-center font-medium rounded-full ${style} ${sizeClass}`}>
      <Icon className={size === 'lg' ? 'h-4 w-4 mr-1.5' : 'h-3 w-3 mr-1'} />
      {label}
    </span>
  )
}

// Plan Badge
const PlanBadge = ({ plan }) => {
  const styles = {
    trial: 'bg-gray-100 text-gray-700',
    starter: 'bg-blue-100 text-blue-700',
    professional: 'bg-purple-100 text-purple-700',
    enterprise: 'bg-amber-100 text-amber-700'
  }

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[plan] || styles.trial}`}>
      {plan}
    </span>
  )
}

// Stats Card
const StatsCard = ({ title, value, icon: Icon, color = 'primary', subtitle }) => {
  const colors = {
    primary: 'bg-primary-50 text-primary-600',
    green: 'bg-green-50 text-green-600',
    blue: 'bg-blue-50 text-blue-600',
    amber: 'bg-amber-50 text-amber-600',
    purple: 'bg-purple-50 text-purple-600'
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-gray-500">{title}</span>
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
    </div>
  )
}

// Compliance Section: stato firme documenti legali per organization
const ComplianceSection = ({ organizationId }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [revokingId, setRevokingId] = useState(null)

  const load = async () => {
    try {
      setLoading(true)
      const res = await adminApi.getOrganizationCompliance(organizationId)
      setData(res.data?.data || null)
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel caricamento compliance')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [organizationId])

  const handleRevoke = async (exceptionId) => {
    if (!confirm('Revocare questa eccezione di compliance?')) return
    try {
      setRevokingId(exceptionId)
      await adminApi.revokeComplianceException(organizationId, exceptionId)
      await load()
    } catch (err) {
      alert(err.response?.data?.message || 'Errore nella revoca')
    } finally {
      setRevokingId(null)
    }
  }

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—'

  const getStatusBadge = (status, doc) => {
    const map = {
      signed: { cls: 'bg-green-100 text-green-700', label: 'Firmato' },
      pending: { cls: 'bg-yellow-100 text-yellow-700', label: 'In attesa' },
      overdue: { cls: 'bg-red-100 text-red-700', label: 'Scaduto' },
      extended: { cls: 'bg-blue-100 text-blue-700', label: `In proroga fino al ${formatDate(doc.exception?.extendedUntil)}` },
      exempted: { cls: 'bg-gray-100 text-gray-700', label: 'Esonerato' }
    }
    const it = map[status] || map.pending
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${it.cls}`}>
        {it.label}
      </span>
    )
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary-600" />
          Conformità documenti
        </h3>
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary-600" />
          Conformità documenti
        </h3>
        <p className="text-sm text-red-600">{error || 'Dati non disponibili'}</p>
      </div>
    )
  }

  const overallBadge = data.isReadOnly
    ? { cls: 'bg-red-100 text-red-700', icon: XCircle, label: 'Sola-lettura' }
    : data.isCompliant
      ? { cls: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Conforme' }
      : { cls: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'In attesa firma' }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary-600" />
          Conformità documenti
        </h3>
        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${overallBadge.cls}`}>
          <overallBadge.icon className="h-3.5 w-3.5" />
          {overallBadge.label}
        </span>
      </div>

      {data.documents.length === 0 ? (
        <p className="text-sm text-gray-500">Nessun documento required attivo a livello organization.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Titolo</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Versione</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Stato</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Firmato il</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Deadline</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.documents.map(doc => (
                <tr key={doc.documentId}>
                  <td className="px-3 py-2 text-sm">
                    <p className="font-medium text-gray-900">{doc.title}</p>
                    <p className="text-xs text-gray-500">{doc.typeDisplayName} · {doc.blockType}</p>
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-600">v{doc.version}</td>
                  <td className="px-3 py-2">{getStatusBadge(doc.status, doc)}</td>
                  <td className="px-3 py-2 text-sm text-gray-600">{formatDate(doc.signedAt)}</td>
                  <td className="px-3 py-2 text-sm text-gray-600">
                    {formatDate(doc.signatureDeadline)}
                    {doc.isOverdue && (
                      <span className="ml-1 text-xs text-red-600">(+{doc.daysOverdue}gg)</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {doc.exception ? (
                      <button
                        onClick={() => handleRevoke(doc.exception._id)}
                        disabled={revokingId === doc.exception._id}
                        className="px-2 py-1 text-xs border border-orange-500 text-orange-600 rounded hover:bg-orange-50 disabled:opacity-50"
                      >
                        {revokingId === doc.exception._id ? '...' : 'Revoca'}
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.documents.some(d => !d.isSigned && !d.exception) && (
        <p className="mt-3 text-xs text-gray-500">
          💡 Per gestire estensioni o esoneri usa la tab "Non conformi" della pagina Documenti Legali.
        </p>
      )}
    </div>
  )
}

// Modal Component
const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl'
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
        <div className="fixed inset-0 bg-gray-900/50" onClick={onClose}></div>
        <div className={`relative inline-block w-full ${sizeClasses[size]} p-6 my-8 text-left bg-white rounded-xl shadow-xl`}>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
          {children}
        </div>
      </div>
    </div>
  )
}

export default function OrganizationDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [organization, setOrganization] = useState(null)
  const [centers, setCenters] = useState([])
  const [stats, setStats] = useState(null)
  const [migrations, setMigrations] = useState({ incoming: [], outgoing: [] })
  
  // Modals
  const [showSuspendModal, setShowSuspendModal] = useState(false)
  const [showExtendTrialModal, setShowExtendTrialModal] = useState(false)
  const [suspendReason, setSuspendReason] = useState('')
  const [extendDays, setExtendDays] = useState(14)
  const [actionLoading, setActionLoading] = useState(false)

  useEffect(() => {
    loadData()
  }, [id])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const [detailRes, migrationsRes] = await Promise.all([
        adminApi.getOrganization(id),
        adminApi.getOrganizationMigrations(id)
      ])
      
      setOrganization(detailRes.data.data.organization)
      setCenters(detailRes.data.data.centers)
      setStats(detailRes.data.data.stats)
      setMigrations(migrationsRes.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  const handleSuspend = async () => {
    if (!suspendReason.trim() || suspendReason.length < 10) {
      alert('Inserisci un motivo valido (min 10 caratteri)')
      return
    }
    
    try {
      setActionLoading(true)
      await adminApi.suspendOrganization(id, suspendReason)
      setShowSuspendModal(false)
      setSuspendReason('')
      loadData()
    } catch (err) {
      alert(err.response?.data?.message || 'Errore durante la sospensione')
    } finally {
      setActionLoading(false)
    }
  }

  const handleReactivate = async () => {
    if (!confirm('Sei sicuro di voler riattivare questa organization e tutti i suoi centri?')) {
      return
    }
    
    try {
      setActionLoading(true)
      await adminApi.reactivateOrganization(id)
      loadData()
    } catch (err) {
      alert(err.response?.data?.message || 'Errore durante la riattivazione')
    } finally {
      setActionLoading(false)
    }
  }

  const handleExtendTrials = async () => {
    try {
      setActionLoading(true)
      await adminApi.extendOrganizationTrials(id, extendDays)
      setShowExtendTrialModal(false)
      loadData()
    } catch (err) {
      alert(err.response?.data?.message || 'Errore durante l\'estensione')
    } finally {
      setActionLoading(false)
    }
  }

  const handleApproveMigration = async (centerId) => {
    if (!confirm('Approvare questa migrazione?')) return
    
    try {
      await adminApi.approveOrganizationMigration(id, centerId)
      loadData()
    } catch (err) {
      alert(err.response?.data?.message || 'Errore')
    }
  }

  const handleRejectMigration = async (centerId) => {
    const reason = prompt('Motivo del rifiuto (min 10 caratteri):')
    if (!reason || reason.length < 10) {
      alert('Motivo non valido')
      return
    }
    
    try {
      await adminApi.rejectOrganizationMigration(id, centerId, reason)
      loadData()
    } catch (err) {
      alert(err.response?.data?.message || 'Errore')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-red-800 mb-2">Errore</h3>
        <p className="text-red-600">{error}</p>
        <button 
          onClick={() => navigate('/organizations')}
          className="mt-4 text-red-600 hover:text-red-700"
        >
          Torna alla lista
        </button>
      </div>
    )
  }

  const trialsCount = centers.filter(c => c.status === 'trial').length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/organizations"
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">
                {organization.name}
              </h1>
              <StatusBadge status={organization.status} size="lg" />
            </div>
            <p className="text-gray-500">
              {organization.legalName || organization.slug}
            </p>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2">
          {organization.status === 'suspended' ? (
            <button
              onClick={handleReactivate}
              disabled={actionLoading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center disabled:opacity-50"
            >
              <PlayCircle className="h-4 w-4 mr-2" />
              Riattiva
            </button>
          ) : organization.status === 'active' && (
            <button
              onClick={() => setShowSuspendModal(true)}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center"
            >
              <Pause className="h-4 w-4 mr-2" />
              Sospendi
            </button>
          )}
          
          {trialsCount > 0 && (
            <button
              onClick={() => setShowExtendTrialModal(true)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
            >
              <Clock className="h-4 w-4 mr-2" />
              Estendi Trial ({trialsCount})
            </button>
          )}
          
          <button
            onClick={loadData}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="h-5 w-5 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatsCard 
          title="Centri" 
          value={stats?.centersCount || 0}
          icon={Building2}
          color="primary"
        />
        <StatsCard 
          title="Minori" 
          value={stats?.totalMinori || 0}
          icon={Users}
          color="blue"
        />
        <StatsCard 
          title="Registrazioni" 
          value={stats?.totalEntries || 0}
          icon={FileText}
          color="green"
        />
        <StatsCard 
          title="Utenti" 
          value={stats?.totalUsers || 0}
          icon={UserCheck}
          color="purple"
        />
        <StatsCard 
          title="MRR" 
          value={`€${(stats?.mrr / 100 || 0).toFixed(2)}`}
          icon={Euro}
          color="amber"
          subtitle="Mensile stimato"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Info Organization */}
        <div className="lg:col-span-1 space-y-6">
          {/* Dati Generali */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Informazioni
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Building className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Ragione Sociale</p>
                  <p className="font-medium">{organization.legalName || '-'}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">P.IVA</p>
                  <p className="font-medium">{organization.fiscal?.partitaIva || '-'}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{organization.contacts?.email || '-'}</p>
                </div>
              </div>
              
              {organization.contacts?.phone && (
                <div className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Telefono</p>
                    <p className="font-medium">{organization.contacts.phone}</p>
                  </div>
                </div>
              )}
              
              {organization.contacts?.website && (
                <div className="flex items-start gap-3">
                  <Globe className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Sito Web</p>
                    <a 
                      href={organization.contacts.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="font-medium text-primary-600 hover:underline"
                    >
                      {organization.contacts.website}
                    </a>
                  </div>
                </div>
              )}
              
              {organization.legalAddress?.city && (
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Sede Legale</p>
                    <p className="font-medium">
                      {organization.legalAddress.street && `${organization.legalAddress.street}, `}
                      {organization.legalAddress.city} 
                      {organization.legalAddress.province && ` (${organization.legalAddress.province})`}
                    </p>
                  </div>
                </div>
              )}
              
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Registrata il</p>
                  <p className="font-medium">
                    {new Date(organization.createdAt).toLocaleDateString('it-IT', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Responsabile */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Responsabile
            </h2>
            {organization.responsabileId ? (
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-lg font-medium text-primary-600">
                    {organization.responsabileId.nome?.[0]}{organization.responsabileId.cognome?.[0]}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {organization.responsabileId.nome} {organization.responsabileId.cognome}
                  </p>
                  <p className="text-sm text-gray-500">{organization.responsabileId.email}</p>
                  {organization.responsabileId.lastLogin && (
                    <p className="text-xs text-gray-400 mt-1">
                      Ultimo accesso: {new Date(organization.responsabileId.lastLogin).toLocaleDateString('it-IT')}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-gray-500">Nessun responsabile assegnato</p>
            )}
          </div>

          {/* Distribuzione Piani */}
          {stats?.centersByPlan && Object.keys(stats.centersByPlan).length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Distribuzione Piani
              </h2>
              <div className="space-y-3">
                {Object.entries(stats.centersByPlan).map(([plan, count]) => (
                  <div key={plan} className="flex items-center justify-between">
                    <PlanBadge plan={plan} />
                    <span className="font-medium">{count} centri</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Centri e Migrazioni */}
        <div className="lg:col-span-2 space-y-6">
          {/* Lista Centri */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">
                Centri ({centers.length})
              </h2>
            </div>
            
            {centers.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>Nessun centro associato</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {centers.map((center) => (
                  <div key={center._id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-primary-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{center.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <StatusBadge status={center.status} />
                            <PlanBadge plan={center.currentPlan} />
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {center.status === 'trial' && center.trialEndDate && (
                          <span className="text-xs text-gray-500">
                            Scade: {new Date(center.trialEndDate).toLocaleDateString('it-IT')}
                          </span>
                        )}
                        <Link
                          to={`/tenants/${center._id}`}
                          className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                    {center.coordinatoreId && (
                      <p className="text-sm text-gray-500 mt-2 ml-14">
                        Coordinatore: {center.coordinatoreId.nome} {center.coordinatoreId.cognome}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Migrazioni Pendenti */}
          {(migrations.incoming.length > 0 || migrations.outgoing.length > 0) && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 border-b border-gray-100 bg-amber-50">
                <div className="flex items-center gap-2">
                  <ArrowRightLeft className="h-5 w-5 text-amber-600" />
                  <h2 className="text-lg font-semibold text-gray-900">
                    Migrazioni Pendenti ({migrations.incoming.length + migrations.outgoing.length})
                  </h2>
                </div>
              </div>
              
              <div className="divide-y divide-gray-100">
                {/* Incoming */}
                {migrations.incoming.map((migration) => (
                  <div key={migration._id} className="p-4 bg-green-50/50">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                            IN ARRIVO
                          </span>
                          <span className="font-medium">{migration.name}</span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          Da: {migration.organizationId?.name || 'N/A'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleApproveMigration(migration._id)}
                          className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
                        >
                          Approva
                        </button>
                        <button
                          onClick={() => handleRejectMigration(migration._id)}
                          className="px-3 py-1 border border-red-300 text-red-600 text-sm rounded-lg hover:bg-red-50"
                        >
                          Rifiuta
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Outgoing */}
                {migrations.outgoing.map((migration) => (
                  <div key={migration._id} className="p-4 bg-blue-50/50">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                            IN USCITA
                          </span>
                          <span className="font-medium">{migration.name}</span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          Verso: {migration.migration?.targetOrganizationId?.name || 'N/A'}
                        </p>
                      </div>
                      <span className="text-xs text-gray-500">
                        Richiesta: {new Date(migration.migration?.requestedAt).toLocaleDateString('it-IT')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Conformità documenti legali */}
      <ComplianceSection organizationId={id} />

      {/* Modal Sospensione */}
      <Modal
        isOpen={showSuspendModal}
        onClose={() => setShowSuspendModal(false)}
        title="Sospendi Organization"
      >
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>Attenzione:</strong> La sospensione bloccherà l'accesso a TUTTI i centri 
              dell'organization ({centers.length} centri).
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Motivo della sospensione *
            </label>
            <textarea
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              rows={3}
              placeholder="Inserisci il motivo della sospensione (min 10 caratteri)..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={() => setShowSuspendModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Annulla
            </button>
            <button
              onClick={handleSuspend}
              disabled={actionLoading || suspendReason.length < 10}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
            >
              {actionLoading ? 'Sospensione...' : 'Sospendi Organization'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal Estendi Trial */}
      <Modal
        isOpen={showExtendTrialModal}
        onClose={() => setShowExtendTrialModal(false)}
        title="Estendi Trial"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Estendi il periodo di trial per tutti i <strong>{trialsCount} centri</strong> 
            attualmente in trial.
          </p>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Giorni da aggiungere
            </label>
            <input
              type="number"
              value={extendDays}
              onChange={(e) => setExtendDays(parseInt(e.target.value) || 0)}
              min={1}
              max={365}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={() => setShowExtendTrialModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Annulla
            </button>
            <button
              onClick={handleExtendTrials}
              disabled={actionLoading || extendDays < 1}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {actionLoading ? 'Estensione...' : `Estendi di ${extendDays} giorni`}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
